import { useMemo, useState, type CSSProperties } from 'react';
import type { PlanBlobWeek, PlanBlobDay, PlanWorkout } from '../api.js';
import { fmtPacePref, fmtDistanceKm } from '../lib/trainingFormat.js';
import type { UnitPref } from '../lib/units.js';
import { Button, Icon, Sheet } from '../ui/index.js';
import { localYmd } from '../lib/foodPage.js';

const WEEKDAYS = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];
/** Weeks drawn before "Show all weeks" (from the current week on). */
const WEEKS_SHOWN = 4;

interface Props {
  weeks: PlanBlobWeek[];
  planWorkouts: PlanWorkout[];
  /** Toggle a runnable day's completion. */
  onToggle: (planWorkoutId: string, completed: boolean) => void;
  busyId?: string | null;
  unitPref?: UnitPref;
}

/** Short day-of-month for a YYYY-MM-DD string (no timezone drift). */
function dayNum(date: string): string {
  return String(Number(date.slice(8, 10)));
}

function phaseColor(phase: string): string {
  return `var(--phase-${phase}, var(--accent))`;
}

/**
 * The plan as calm week rows: a phase label, then seven day cells (run days
 * tinted with the phase colour, done days filled with a check). Tapping a day
 * opens its detail in a sheet with the one action, Mark done. Defaults to the
 * current week and the next few; the rest are a tap away.
 */
export default function PlanCalendar({
  weeks,
  planWorkouts,
  onToggle,
  busyId,
  unitPref = 'imperial',
}: Props): JSX.Element {
  const byDate = useMemo(() => {
    const m = new Map<string, PlanWorkout>();
    for (const w of planWorkouts) m.set(w.date, w);
    return m;
  }, [planWorkouts]);

  const today = localYmd(new Date());
  const [showAll, setShowAll] = useState(false);
  const [selected, setSelected] = useState<string | null>(null);

  // First week whose last day is today or later; past plans show their end.
  const currentIdx = useMemo(() => {
    const i = weeks.findIndex((w) => (w.days[w.days.length - 1]?.date ?? '') >= today);
    return i === -1 ? Math.max(0, weeks.length - WEEKS_SHOWN) : i;
  }, [weeks, today]);
  const visible = showAll ? weeks : weeks.slice(currentIdx, currentIdx + WEEKS_SHOWN);
  const hidden = weeks.length - visible.length;

  const selectedDay = useMemo(() => {
    if (!selected) return null;
    for (const wk of weeks) {
      const d = wk.days.find((x) => x.date === selected);
      if (d) return { day: d, week: wk, row: byDate.get(d.date) ?? null };
    }
    return null;
  }, [selected, weeks, byDate]);

  return (
    <div className="plancal2">
      <div className="plancal2__dows" aria-hidden="true">
        <span />
        {WEEKDAYS.map((d, i) => (
          <span key={i}>{d}</span>
        ))}
      </div>
      <div role="grid" aria-label="Training plan calendar">
        {visible.map((wk) => (
          <div
            className={`plancal2__week${wk.days.some((d) => d.date === today) ? ' plancal2__week--now' : ''}`}
            role="row"
            key={wk.weekNumber}
            style={{ '--phase': phaseColor(wk.phase) } as CSSProperties}
          >
            <div className="plancal2__wk" title={wk.phaseName}>
              <span className="plancal2__wknum">W{wk.weekNumber}</span>
              <span className="plancal2__phase">{wk.phase}</span>
            </div>
            {wk.days.map((day) => {
              const row = byDate.get(day.date);
              const cls = [
                'plancal2__cell',
                day.isRun ? 'is-run' : 'is-rest',
                row?.completed ? 'is-done' : '',
                day.date === today ? 'is-today' : '',
                day.date < today && day.isRun && !row?.completed ? 'is-missed' : '',
              ]
                .filter(Boolean)
                .join(' ');
              const label = `${day.type}, ${new Date(`${day.date}T00:00:00`).toLocaleDateString(undefined, {
                weekday: 'long',
                month: 'short',
                day: 'numeric',
              })}${row?.completed ? ', done' : ''}`;
              return (
                <button
                  key={day.date}
                  type="button"
                  role="gridcell"
                  className={cls}
                  onClick={() => setSelected(day.date)}
                  aria-label={label}
                  title={label}
                >
                  {row?.completed ? <Icon name="check" size={16} /> : <span className="num">{dayNum(day.date)}</span>}
                </button>
              );
            })}
          </div>
        ))}
      </div>
      {hidden > 0 && (
        <button type="button" className="plancal2__more" onClick={() => setShowAll(true)}>
          Show all {weeks.length} weeks
        </button>
      )}
      {showAll && weeks.length > WEEKS_SHOWN && (
        <button type="button" className="plancal2__more" onClick={() => setShowAll(false)}>
          Show fewer
        </button>
      )}

      {selectedDay && (
        <DayDetail
          day={selectedDay.day}
          phase={selectedDay.week.phase}
          phaseName={selectedDay.week.phaseName}
          row={selectedDay.row}
          busy={!!selectedDay.row && busyId === selectedDay.row.id}
          onToggle={onToggle}
          onClose={() => setSelected(null)}
          unitPref={unitPref}
        />
      )}
    </div>
  );
}

function DayDetail({
  day,
  phase,
  phaseName,
  row,
  busy,
  onToggle,
  onClose,
  unitPref,
}: {
  day: PlanBlobDay;
  phase: string;
  phaseName: string;
  row: PlanWorkout | null;
  busy: boolean;
  onToggle: (planWorkoutId: string, completed: boolean) => void;
  onClose: () => void;
  unitPref: UnitPref;
}): JSX.Element {
  const pace = day.targetPaceSecPerKm ?? row?.targetPaceSecPerKm ?? null;
  return (
    <Sheet title={day.type} onClose={onClose}>
      <div className="plancal2__detail" style={{ '--phase': phaseColor(phase) } as CSSProperties}>
        <p className="plancal2__date">
          {new Date(`${day.date}T00:00:00`).toLocaleDateString(undefined, {
            weekday: 'long',
            month: 'long',
            day: 'numeric',
          })}
          <span className="plancal2__phasepill">{phaseName || phase}</span>
        </p>

        {(day.distanceKm != null || (pace != null && pace > 0)) && (
          <div className="plancal2__facts">
            {day.distanceKm != null && (
              <div>
                <span className="plancal2__factval num">{fmtDistanceKm(day.distanceKm, unitPref)}</span>
                <span className="plancal2__factlab">Distance</span>
              </div>
            )}
            {pace != null && pace > 0 && (
              <div>
                <span className="plancal2__factval num">{fmtPacePref(pace, unitPref)}</span>
                <span className="plancal2__factlab">Target pace</span>
              </div>
            )}
          </div>
        )}

        {day.description && (
          <p className="plancal2__desc">
            {day.description.split('\n').map((line, i) => (
              <span key={i}>
                {line}
                <br />
              </span>
            ))}
          </p>
        )}

        {row?.completed && row.completedSource === 'auto' && (
          <p className="hb-hint">Auto-linked from a real run.</p>
        )}

        {day.isRun && row && (
          <Button
            variant={row.completed ? 'secondary' : 'primary'}
            size="lg"
            block
            icon={row.completed ? 'undo' : 'check'}
            disabled={busy}
            onClick={() => onToggle(row.id, !row.completed)}
          >
            {row.completed ? 'Mark not done' : 'Mark done'}
          </Button>
        )}
      </div>
    </Sheet>
  );
}
